import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import AuroraMaxTemperatureLimitControl from './AuroraMaxTemperatureLimitControl'
import { AuroraAssetGroup, AuroraSetMaxTemperatureLimitActionInput } from '../types'
import { useAuroraStack } from '../hooks/aurora'
import { publishAuroraAwhpAction } from '../services/aurora'
import { createSuccessNotification } from '../redux/slices/notifications'

export interface AuroraAssetGroupMaxTemperatureLimitControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (input: AuroraSetMaxTemperatureLimitActionInput) => void
  afterSubmit?: (value: number) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupMaxTemperatureLimitControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupMaxTemperatureLimitControlProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const {
    auroras,
    maxTemperatureLimit: initialMaxTemperatureLimit,
    handleAuroraAwhpActionError
  } = useAuroraStack(auroraAssetGroup)

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const [maxMaxTemperatureLimit, setMaxTemperatureLimit] = useState<number | null>(
    initialMaxTemperatureLimit
  )

  async function handleSubmit(input: AuroraSetMaxTemperatureLimitActionInput) {
    try {
      setSubmitting(true)

      beforeSubmit?.(input)

      await Promise.all(auroras.map((aurora) => publishAuroraAwhpAction(aurora.id, input)))

      dispatch(createSuccessNotification(t('setMaxTemperatureLimitSuccess')))
      setMaxTemperatureLimit(input.alarms.maxTempLimit)
      afterSubmit?.(input.alarms.maxTempLimit)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(error, t('setMaxTemperatureLimitError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuroraMaxTemperatureLimitControl
      value={maxMaxTemperatureLimit}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
